import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Users, UserCheck, UserX, TrendingUp, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export default function MembersOverview() {
  const { currentBranch } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bq = currentBranch ? `?branchId=${currentBranch.id}` : "";
        const [statsRes, dashRes] = await Promise.all([
          api.get(`/members/stats${bq}`),
          api.get(`/dashboard/stats${bq}`),
        ]);
        setStats({ ...statsRes.data, ...dashRes.data });
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [currentBranch]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  const statusData = [
    { name: "Active", value: stats?.active || 0, color: "hsl(var(--chart-2))" },
    { name: "Expired", value: stats?.expired || 0, color: "hsl(var(--primary))" },
    { name: "Partial", value: stats?.partial || 0, color: "hsl(var(--chart-3))" },
    { name: "Inactive", value: stats?.inactive || 0, color: "hsl(var(--muted-foreground))" },
  ];

  const genderData = [
    { name: "Male", value: stats?.maleCount || 0 },
    { name: "Female", value: stats?.femaleCount || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="members-overview-page">
      <div>
        <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Members Overview</h1>
        <p className="text-xs text-muted-foreground mt-1">Analytics and insights about your gym members</p>
      </div>

      {/* Key Metrics — 5 cards: Total, Active, Expired, Partial, New This Month */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <Users className="h-5 w-5 text-muted-foreground" />
            <p className="text-3xl font-black font-heading mt-3">{stats?.total || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">Total Members</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <UserCheck className="h-5 w-5 text-emerald-500" />
            <p className="text-3xl font-black font-heading mt-3 text-emerald-500">{stats?.active || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <UserX className="h-5 w-5 text-red-500" />
            <p className="text-3xl font-black font-heading mt-3 text-red-500">{stats?.expired || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">Expired</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <Clock className="h-5 w-5 text-amber-500" />
            <p className="text-3xl font-black font-heading mt-3 text-amber-500">{stats?.partial || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">Partial</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <TrendingUp className="h-5 w-5 text-primary" />
            <p className="text-3xl font-black font-heading mt-3">{stats?.newThisMonth || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">New This Month</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {statusData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-2">
              {statusData.map((s) => (
                <div key={s.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-xs text-muted-foreground">{s.name}: {s.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gender Distribution */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Gender Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={genderData} layout="vertical" barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} width={60} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
